/**
 * Preset Selector Component
 *
 * Allows users to quickly switch between configuration presets:
 * - Minimal: Basic dialog functionality (2 hooks)
 * - Standard: Personalization + Astrology (5 hooks)
 * - Advanced: All features enabled
 */

import React from 'react';
import { Check, Zap, Star, Settings } from 'lucide-react';
import {
  CONFIG_PRESETS,
  getPresetHooksConfig,
  type PresetId,
  type SelectablePresetId,
} from '@/config/hooks-registry';

interface PresetSelectorProps {
  currentPreset: PresetId;
  onPresetChange: (presetId: SelectablePresetId, config: Record<string, boolean | string>) => void;
  className?: string;
}

// Only the 3 selectable presets — 'custom' is inferred, not clickable
const SELECTABLE_PRESETS: ('minimal' | 'standard' | 'advanced')[] = ['minimal', 'standard', 'advanced'];

const PRESET_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  minimal: Zap,
  standard: Star,
  advanced: Settings,
};

const PRESET_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  minimal: {
    bg: 'bg-gray-50 hover:bg-gray-100',
    border: 'border-gray-200',
    text: 'text-gray-700',
  },
  standard: {
    bg: 'bg-blue-50 hover:bg-blue-100',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  advanced: {
    bg: 'bg-purple-50 hover:bg-purple-100',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
};

export const PresetSelector: React.FC<PresetSelectorProps> = ({
  currentPreset,
  onPresetChange,
  className = '',
}) => {
  const handlePresetClick = (presetId: SelectablePresetId) => {
    if (presetId === currentPreset) return;
    const config = getPresetHooksConfig(presetId);
    onPresetChange(presetId, config);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">配置预设</h3>
        <span className="text-xs text-gray-500">快速切换功能配置</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {SELECTABLE_PRESETS.map((presetId) => {
          const preset = CONFIG_PRESETS[presetId];
          const Icon = PRESET_ICONS[presetId];
          const colors = PRESET_COLORS[presetId];
          const isSelected = currentPreset === presetId;

          return (
            <button
              key={presetId}
              onClick={() => handlePresetClick(presetId)}
              className={`
                relative p-4 rounded-lg border-2 transition-all text-left
                ${isSelected
                  ? `${colors.bg} ${colors.border} ring-2 ring-offset-1 ring-${presetId === 'minimal' ? 'gray' : presetId === 'standard' ? 'blue' : 'purple'}-300`
                  : `bg-white border-gray-200 hover:border-gray-300`
                }
              `}
            >
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="w-4 h-4 text-green-500" />
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${isSelected ? colors.text : 'text-gray-400'}`} />
                <span className={`font-medium ${isSelected ? colors.text : 'text-gray-700'}`}>
                  {preset.name}
                </span>
              </div>

              <p className="text-xs text-gray-500 line-clamp-2">
                {preset.description}
              </p>

              <div className="mt-2 text-xs text-gray-400">
                {preset.enabledHooks.length} 个功能
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 text-center">
        {currentPreset === 'custom'
          ? '当前为自定义配置，选择预设可快速切换'
          : '切换预设会自动调整下方功能的启用状态'}
      </p>
    </div>
  );
};

export default PresetSelector;
